import { useEffect, useState } from 'react'
import { Alert, Button, Empty, Flex, Modal, Skeleton, Typography, theme } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { displayName } from '@/shared/lib/planName'
import { LoadError } from '@/shared/ui/LoadError'
import { fetchDeniedPlans, fetchPlans, issueGrant, type GrantOutcome, type Subscriber } from './subscriberApi'
import { subscriberKeys } from './subscriberKeys'

interface SubmitButtonProps {
  label: string
  running: boolean
  disabled: boolean
  onSubmit: (idempotencyKey: string) => void
}

/**
 * Tasdiq tugmasi — VA `Idempotency-Key` NING UMRI.
 *
 * ⚠️ Kalit `useState` ning BOSHLANG'ICH qiymatida yasaladi, ya'ni
 * komponent mount bo'lganda BIR MARTA. Chaqiruvchi bunga
 * `key={`${subscriberId}:${planId}`}` beradi — ya'ni kalit AMAL
 * IMZOSIGA bog'langan, so'rovga emas:
 *
 *   · tugma qayta bosilsa yoki tarmoq uzilib qayta urinilsa — o'sha
 *     mount, o'sha kalit, ya'ni server ikkinchi so'rovni TAKROR deb
 *     taniydi (200 + `replay:true`) va abonentdan ikkinchi marta pul
 *     yechmaydi;
 *   · abonent yoki tarif o'zgarsa — `key` o'zgaradi, React komponentni
 *     qayta mount qiladi va YANGI kalit yasaladi, ya'ni haqiqiy
 *     ikkinchi sotuv «dublikat» deb jimgina rad etilmaydi.
 *
 * Ikkala tomon ham kerak, va aynan shuning uchun kalit so'rov
 * funksiyasining ichida yasalmaydi (`docs/02-LEDGER.md` §6).
 *
 * ⚠️ Naqsh `features/cashdesk/ConfirmAction.tsx` dan O'RGANILDI, KODI
 * ko'chirilmadi: u yerda tugma ikki bosqichli (bosish → xulosa →
 * tasdiq), chunki kassir abonent qarshisida turadi va oyna ocha
 * olmaydi. Bu yerda amal ALLAQACHON alohida oynada va xulosa ekranda
 * turibdi — ikkinchi tasdiq bosqichi shunchaki uchinchi bosish
 * bo'lardi.
 */
function SubmitButton({ label, running, disabled, onSubmit }: SubmitButtonProps) {
  const [idempotencyKey] = useState(() => newIdempotencyKey('sherik-grant'))
  return (
    <Button
      type="primary"
      block
      size="large"
      loading={running}
      disabled={disabled}
      style={{ height: 48, fontWeight: 600 }}
      onClick={() => onSubmit(idempotencyKey)}
    >
      {label}
    </Button>
  )
}

interface GrantIssueModalProps {
  subscriber: Subscriber | null
  /** Abonentning balansi — paket shundan to'lanadi */
  balance: Money | null
  onClose: () => void
}

/**
 * Paket berish — pul ABONENTNING o'z balansidan yechiladi.
 *
 * ⚠️ Bu «balans to'ldirish» EMAS va bu farq shu modulda ayniqsa muhim:
 * reseller pul to'ldira OLMAYDI (`agent.fund` uning to'plamida yo'q,
 * ADR-029 §1), ya'ni balansi yetmagan abonent uchun to'g'ri javob —
 * arzonroq paket yoki mijozning kassaga borishi, «yana bir marta
 * urinish» emas.
 *
 * <h2>⚠️ 2026-08-21 DAN BUYON FAQAT KASSASIZ SHERIKDA OCHILADI</h2>
 * `subscriber.directory` bor sherikda «Paket berish» endi Kassaga
 * yo'naltiradi (`SubscribersPage`), chunki u yerda balans va paket
 * BITTA tasdiq ostida (`GrantPanel` + `useCombinedSale`). Bu oyna esa
 * O'CHIRILMADI va o'chirilmaydi: kassa huquqisiz resellerda paket
 * berishning YAGONA yo'li shu.
 */
export function GrantIssueModal({ subscriber, balance, onClose }: GrantIssueModalProps) {
  const { t, i18n } = useTranslation()
  const { token } = theme.useToken()
  const queryClient = useQueryClient()
  const [planId, setPlanId] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<GrantOutcome | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [running, setRunning] = useState(false)

  // ⚠️ Oyna YOPILGANDA komponent unmount BO'LMAYDI (faqat `Modal` ning
  // ichi yo'q qilinadi), ya'ni tanlangan tarif va oldingi natija
  // holatda qolib ketardi — keyingi abonent uchun ochilganda ekranda
  // «berildi» degan begona xabar turardi.
  const subscriberId = subscriber?.id ?? null
  useEffect(() => {
    setPlanId(null)
    setOutcome(null)
    setErrorText(null)
  }, [subscriberId])

  const plans = useQuery({
    queryKey: subscriberKeys.plans,
    queryFn: fetchPlans,
    // Katalog kamdan-kam o'zgaradi, oyna esa kuniga ko'p marta ochiladi
    staleTime: 5 * 60_000,
    enabled: subscriber !== null,
  })

  const denied = useQuery({
    queryKey: subscriberKeys.deniedPlans(subscriber?.branchNodeId ?? null),
    queryFn: () => fetchDeniedPlans(subscriber?.branchNodeId ?? null),
    staleTime: 60_000,
    enabled: subscriber !== null,
  })

  const sellable = (plans.data ?? [])
    .filter((plan) => plan.sellable)
    .filter((plan) => !denied.data?.has(plan.id))
  const selected = sellable.find((plan) => plan.id === planId) ?? null

  /**
   * ⚠️ Yetishmovchilik faqat VALYUTALAR MOS bo'lganda hisoblanadi.
   * Turli valyutadagi ikki sonni solishtirish «pul yetmaydi» degan
   * yolg'on ogohlantirish berardi — haqiqiy qarorni baribir server
   * qabul qiladi.
   */
  const short =
    selected !== null &&
    balance !== null &&
    balance.currency === selected.price.currency &&
    balance.amount < selected.price.amount

  const submit = (idempotencyKey: string) => {
    if (subscriber === null || selected === null) return
    setRunning(true)
    setOutcome(null)
    setErrorText(null)
    issueGrant(subscriber.id, selected.id, idempotencyKey)
      .then((result) => {
        setOutcome(result)
        if (result.kind !== 'INSUFFICIENT_FUNDS') {
          void queryClient.invalidateQueries({ queryKey: subscriberKeys.all })
        }
      })
      .catch((error: unknown) => {
        // 400 (kalitsiz), 409 (`IDEMPOTENCY_CONFLICT`), 403, 5xx, tarmoq —
        // hammasi tag bo'yicha tarjima qilinadi.
        setErrorText(translateError(error, t))
      })
      .finally(() => {
        setRunning(false)
      })
  }

  const issued = outcome !== null && outcome.kind !== 'INSUFFICIENT_FUNDS'

  return (
    <Modal
      open={subscriber !== null}
      title={t('subscribers.grant.title')}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      {subscriber === null ? null : (
        <Flex vertical gap={12}>
          <Typography.Text type="secondary">
            {t('subscribers.grant.subtitle', { login: subscriber.login })}
          </Typography.Text>
          <Typography.Text>
            {t('subscribers.grant.balance', { amount: formatMoneyValue(balance) })}
          </Typography.Text>

          {/* ⚠️ NATIJA RO'YXATDAN OLDIN — `GrantPanel` dagi bilan AYNI
              tartib. Tugma yopishqoq bo'lgach, ro'yxat ostidagi banner
              uzun katalogda ekrandan chiqib ketardi: reseller «bosdim,
              hech narsa bo'lmadi» deb ikkinchi marta bosardi. */}
          {errorText ? <Alert type="error" showIcon message={errorText} /> : null}

          {outcome?.kind === 'INSUFFICIENT_FUNDS' ? (
            // ⚠️ Bu SERVERNING ANIQ javobi, texnik xato emas — shuning
            // uchun sariq va matni serverdan (unda kerakli/mavjud summa
            // bo'ladi).
            <Alert
              type="warning"
              showIcon
              message={t('subscribers.grant.noFunds')}
              description={outcome.serverText ?? undefined}
            />
          ) : null}

          {outcome?.kind === 'ISSUED' ? (
            <Alert
              type="success"
              showIcon
              message={t('subscribers.grant.issued', {
                login: subscriber.login,
                plan: selected ? displayName(selected, i18n.language) : '',
              })}
            />
          ) : null}

          {outcome?.kind === 'REPLAY' ? (
            // ⚠️ TAKROR — «yana bitta paket berildi» EMAS. Server ayni
            // kalitni tanidi: paket ham, pul yozuvi ham o'sha. Buni
            // «muvaffaqiyat» deb ko'rsatish resellerga ikkinchi paket
            // berilgandek tuyulardi va u mijozdan ikki marta pul olardi
            // (`GrantIssue` javadoc'i).
            <Alert type="info" showIcon message={t('subscribers.grant.replay')} />
          ) : null}

          {plans.isPending ? <Skeleton active paragraph={{ rows: 3 }} /> : null}
          {plans.isError ? <LoadError titleKey="subscribers.grant.plansFailed" error={plans.error} /> : null}

          {plans.isSuccess && sellable.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('subscribers.grant.noPlans')}
            />
          ) : null}

          {sellable.map((plan) => {
            const active = plan.id === planId
            return (
              <div
                key={plan.id}
                role="button"
                tabIndex={0}
                onClick={() => setPlanId(plan.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setPlanId(plan.id)
                }}
                style={{
                  minHeight: 48,
                  padding: '10px 14px',
                  borderRadius: token.borderRadiusLG,
                  border: `1px solid ${active ? token.colorPrimary : token.colorBorderSecondary}`,
                  background: active ? token.colorPrimaryBg : token.colorBgContainer,
                  cursor: 'pointer',
                }}
              >
                <Flex align="center" justify="space-between" gap={12}>
                  <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                    {active ? <CheckOutlined style={{ color: token.colorPrimary }} /> : null}
                    <Typography.Text ellipsis>{displayName(plan, i18n.language)}</Typography.Text>
                  </Flex>
                  <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
                    {formatMoneyValue(plan.price)}
                  </Typography.Text>
                </Flex>
              </div>
            )
          })}

          {/*
            ⚠️ TASDIQ QATORI YOPISHQOQ — `GrantPanel` DAGI AYNI YECHIM.

            Egasi talabi (2026-08-21): paket ko'p bo'lganda tugmaga
            «pastga scroll qilib borish» kerak bo'lmasin. `sellable`
            ro'yxati bu yerda ham CHEKLOVSIZ chiziladi (har qatori 48px +
            12px), ya'ni 15 paketli katalogda tugma ekrandan ~900px pastda
            qolardi.

            ⚠️ `sticky`, `fixed` EMAS — sabab `GrantPanel` da yozilgan va
            u BU YERDA HAM AMAL QILADI: `fixed` element ekranga yopishib,
            telefondagi pastki tab-bar (`MobileTabBar`, 64px + safe-area)
            ustiga tushardi. `sticky` esa O'Z aylanuvchisiga yopishadi —
            bu yerda u `.ant-modal-wrap` (antd unga `overflow: auto`
            beradi), `GrantPanel` da esa `.ant-drawer-body` yoki
            hujjatning o'zi.

            ⚠️ Ro'yxatga balandlik chegarasi + o'z aylanishi RAD ETILDI —
            `GrantPanel` dagi o'sha sabab: aylanuvchi ichidagi ikkinchi
            aylanuvchi barmoq ostida qaysi biri surilayotganini noaniq
            qiladi, balandlik esa o'ylab topilgan son bo'lardi.

            ⚠️ `colorBgElevated` — `Modal` ning sirti aynan shu token,
            ya'ni chok ko'rinmaydi va paket qatorlari tugma ortidan o'tib
            ko'rinmaydi.

            ⚠️ «Balans yetmaydi» ogohlantirishi SHU YERGA ko'chdi: u
            tugmadagi summani izohlaydigan yagona matn va ro'yxat ostida
            qolsa hech kim o'qimasdi.
          */}
          <Flex
            vertical
            gap={8}
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              paddingTop: 12,
              paddingBottom: 'calc(4px + env(safe-area-inset-bottom, 0px))',
              background: token.colorBgElevated,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            {short ? (
              <Alert type="warning" showIcon message={t('subscribers.grant.shortBalance')} />
            ) : null}

            {issued ? (
              <Button block size="large" onClick={onClose}>
                {t('common.close')}
              </Button>
            ) : (
              <SubmitButton
                key={`${subscriber.id}:${planId ?? ''}`}
                label={
                  selected
                    ? t('subscribers.grant.confirm', { amount: formatMoneyValue(selected.price) })
                    : t('subscribers.grant.choosePlan')
                }
                running={running}
                disabled={selected === null || running}
                onSubmit={submit}
              />
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  )
}
